import { Component, inject, ChangeDetectionStrategy, signal, computed } from '@angular/core'
import { NgbActiveOffcanvas, NgbNavModule } from '@ng-bootstrap/ng-bootstrap'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { Me } from '../../../core/interfaces/me/me.interface'
import { AuthService } from '@auth0/auth0-angular'
import { ButtonComponent } from '../button/button.component'
import { TableModule } from '../../directives/table/table.module'
import { FormsModule } from '@angular/forms'

import { MeService } from '../../../core/services/me.service'
import { NotificationPreference } from '../../../core/interfaces/me/notification-preference.interface'
import { ToastrService } from 'ngx-toastr'
import { finalize } from 'rxjs'
import { PosthogService } from '../../../core/services/insights/posthog.service'
import { CompanyDetails } from '../../../core/interfaces/company/company-details.interface'
import { AppStoreService } from '../../../core/app-state.service'

@Component({
    selector: 'sq-user-offcanvas',
    imports: [TableModule, FormsModule, FontAwesomeModule, NgbNavModule, ButtonComponent],
    templateUrl: './user-offcanvas.component.html',
    styleUrl: './user-offcanvas.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class UserOffcanvasComponent {
    activeOffcanvas = inject(NgbActiveOffcanvas)
    #authService = inject(AuthService)
    #meService = inject(MeService)
    #toastr = inject(ToastrService)
    #posthogService = inject(PosthogService)
    #appStore = inject(AppStoreService)

    me: Me | null = null
    company: CompanyDetails | null = null

    isSaving = signal(false)
    isSavingPrivacy = signal(false)

    permissions = computed(() => this.me?.permissions || [])

    getInitials(): string {
        if (!this.me) return ''
        const first = this.me.firstName?.charAt(0) || ''
        const last = this.me.lastName?.charAt(0) || ''
        return (first + last).toUpperCase()
    }

    logout(): void {
        this.activeOffcanvas.close()
        this.#authService.logout()
        this.#posthogService.logoutUser()
    }

    updateNotificationPreferences(preferences: NotificationPreference[]): void {
        this.isSaving.set(true)
        this.#meService
            .updateNotificationPreferences(preferences)
            .pipe(finalize(() => this.isSaving.set(false)))
            .subscribe({
                next: () => {
                    this.#meService.getMe().subscribe() // Refresh user data to get updated preferences
                    this.#toastr.success('Notification preferences updated successfully.', 'Success')
                },
                error: (error) => {
                    console.error('Error updating notification preferences:', error)
                    this.#toastr.error('An error occurred while updating notification preferences.', 'Error')
                },
            })
    }

    onEnableAnalyticsChange(enableAnalytics: boolean): void {
        if (!enableAnalytics && this.me) {
            this.me.enableSessionReplay = false
        }
    }

    updatePrivacyPreferences(enableAnalytics: boolean, enableSessionReplay: boolean): void {
        this.isSavingPrivacy.set(true)
        this.#meService
            .updatePrivacyPreferences(enableAnalytics, enableSessionReplay)
            .pipe(finalize(() => this.isSavingPrivacy.set(false)))
            .subscribe({
                next: () => {
                    this.#appStore.getMe().subscribe({
                        // Refresh app data to get updated preferences
                        error: (error) =>
                            this.#toastr.error(
                                'Error refreshing app state. Please refresh this page to see your latest changes.',
                                'Error'
                            ),
                    })
                    this.#toastr.success('Privacy preferences updated successfully.', 'Success')
                },
                error: (error) => {
                    console.error('Error updating privacy preferences:', error)
                    this.#toastr.error('An error occurred while updating privacy preferences.', 'Error')
                },
            })
    }
}
